import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  createCaseGuideTemplate,
  fetchCaseGuideTemplates,
  updateCaseGuideTemplate,
} from "../../../../api/caseGuides";
import { CaseTagSelector } from "../CaseTagSelector";
import { SaveBar } from "../../../utils/SaveBar";
import { GuideFlowBuilder, GuideFlowField } from "../../../utils/guideFlow/GuideFlowBuilder";
import { normalizeGuideTemplate, prepareGuideTemplateForSave } from "./caseGuideUtils";
import { priorityOptions } from "../../quality/deviations/utils";

const CASE_EFFECT_NODE_DEFINITIONS = [
  { type: "tags", titleKey: "Etiketter", defaultData: { effects: { tag_ids: [] } } },
  { type: "priority", titleKey: "Prioritet", defaultData: { effects: { priority: "1" } } },
  { type: "assignment", titleKey: "Ansvarlige", defaultData: { effects: { user_assigned_id: "", user_responsible_id: "" } } },
  { type: "due_date", titleKey: "Frist", defaultData: { effects: { due_date: "" } } },
  { type: "title_suffix", titleKey: "Titteltillegg", defaultData: { effects: { title_suffix: "" } } },
  {
    type: "chain_case_guide",
    titleKey: "guide.case_chain_effect_title",
    defaultData: { effects: { next_case_guide_template_id: "" } },
  },
];

export default function CaseGuideTemplateEditor({ caseType, generalOnly = false, fullPageLayout = false }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([emptyTemplate(caseType, generalOnly)]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveState, setSaveState] = useState({ status: "idle", error: null });
  const template = templates[selectedIndex] || emptyTemplate(caseType, generalOnly);

  const [chainGuideCandidates, setChainGuideCandidates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchCaseGuideTemplates({ purpose: "onboarding", include_inactive: false }).then((rows) => {
      if (cancelled) return;
      const list = Array.isArray(rows) ? rows : [];
      setChainGuideCandidates(
        list.filter((row) => row?.id && String(row.id) !== String(template?.id) && row.active !== false)
      );
    });
    return () => {
      cancelled = true;
    };
  }, [template?.id]);

  useEffect(() => {
    let mounted = true;
    async function loadTemplate() {
      if (!generalOnly && !caseType?.id) return;
      setIsLoading(true);
      const params = {
        purpose: "onboarding",
        include_inactive: true,
      };
      if (caseType?.id) {
        params.case_type_id = caseType.id;
      }
      const existing = await fetchCaseGuideTemplates(params);
      if (!mounted) return;
      let normalized = existing.map(normalizeGuideTemplate).filter(Boolean);
      if (generalOnly) {
        normalized = normalized.filter((item) => !item.case_type_id);
      } else {
        normalized.sort((a, b) => {
          const aw = a.case_type_id ? 0 : 1;
          const bw = b.case_type_id ? 0 : 1;
          if (aw !== bw) return aw - bw;
          return String(a.title || "").localeCompare(String(b.title || ""));
        });
      }
      setTemplates(normalized.length ? normalized : [emptyTemplate(caseType, generalOnly)]);
      setSelectedIndex(0);
      setHasChanges(false);
      setSaveState({ status: "idle", error: null });
      setIsLoading(false);
    }
    loadTemplate();
    return () => {
      mounted = false;
    };
  }, [caseType?.id, generalOnly]);

  const updateTemplate = (patch) => {
    setHasChanges(true);
    setSaveState({ status: "idle", error: null });
    setTemplates((prev) => prev.map((item, index) => (index === selectedIndex ? { ...item, ...patch } : item)));
  };

  const addGuide = () => {
    const nextIndex = templates.length;
    setTemplates((prev) => [
      ...prev,
      {
        ...emptyTemplate(caseType, generalOnly),
        title: `${caseType?.title || t("Case guide")} ${prev.length + 1}`,
      },
    ]);
    setSelectedIndex(nextIndex);
    setHasChanges(false);
    setSaveState({ status: "idle", error: null });
  };

  const duplicateGuide = () => {
    const nextIndex = templates.length;
    const copy = {
      ...template,
      id: undefined,
      title: `${template.title || t("Case guide")} (${t("kopi")})`,
      questions: (template.questions || []).map((question) => ({ ...question, id: undefined })),
      settings: { ...(template.settings || {}) },
    };
    setTemplates((prev) => [...prev, copy]);
    setSelectedIndex(nextIndex);
    setHasChanges(false);
    setSaveState({ status: "idle", error: null });
  };

  const save = async () => {
    setSaveState({ status: "saving", error: null });
    const payload = prepareGuideTemplateForSave({
      ...template,
      case_type_id: generalOnly ? null : template.case_type_id ?? caseType?.id ?? null,
      purpose: "onboarding",
    });

    const res = template.id
      ? await updateCaseGuideTemplate(template.id, payload)
      : await createCaseGuideTemplate(payload);

    if (res?.errors) {
      const message = Array.isArray(res.errors) ? res.errors.join(", ") : String(res.errors);
      setSaveState({ status: "error", error: message });
      toast.error(message);
      return;
    }

    const normalized = normalizeGuideTemplate(res);
    setTemplates((prev) => prev.map((item, index) => (index === selectedIndex ? normalized : item)));
    setHasChanges(false);
    setSaveState({ status: "saved", error: null });
    toast.success(t("Saved"));
  };

  if (!generalOnly && !caseType?.id) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
        {t("Lagre case type før du konfigurerer guide.")}
      </div>
    );
  }

  return (
    <div
      className={
        fullPageLayout
          ? "w-full max-w-none space-y-4 pt-1"
          : "mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{t("Case guide")}</h3>
          <p className="mt-1 text-xs text-gray-500">
            {generalOnly ? t("Case guide scope general hint") : t("Case guide scope type hint")}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={template.active !== false}
            onChange={(e) => updateTemplate({ active: e.target.checked })}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          {t("Aktiv")}
        </label>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Guider")}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={duplicateGuide}
              className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("Dupliser guide")}
            </button>
            <button
              type="button"
              onClick={addGuide}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {t("Ny guide")}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {templates.map((guide, index) => (
            <button
              key={guide.id || `new-${index}`}
              type="button"
              onClick={() => {
                setSelectedIndex(index);
                setHasChanges(false);
                setSaveState({ status: "idle", error: null });
              }}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ring-1 ${
                selectedIndex === index
                  ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
                  : "bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100"
              }`}
            >
              {guide.title || t("Uten navn")}
              {!guide.case_type_id ? ` · ${t("Generell caseguide")}` : ""}
              {guide.active === false ? ` · ${t("Inaktiv")}` : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          value={template.title || ""}
          onChange={(e) => updateTemplate({ title: e.target.value })}
          placeholder={t("Guide title")}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
        />
        <input
          value={template.description || ""}
          onChange={(e) => updateTemplate({ description: e.target.value })}
          placeholder={t("Guide description")}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
        />
      </div>

      <div className={fullPageLayout ? "case-guide-flow-shell case-guide-flow-shell--full w-full" : "mt-4"}>
        {isLoading ? (
          <p className="text-sm text-gray-500">{t("Loading...")}</p>
        ) : (
          <GuideFlowBuilder
            EffectsEditorComponent={(editorProps) => (
              <CaseGuideEffectsEditor {...editorProps} chainGuideCandidates={chainGuideCandidates} />
            )}
            checklistTypes={[]}
            defaultQuestionData={{ include_in_report: false }}
            effectNodeDefinitions={CASE_EFFECT_NODE_DEFINITIONS}
            effectsOnQuestionOptions={false}
            guideFlowKind="case_onboarding"
            markerIdPrefix="case-guide"
            questionOptionsCaption={t("Case guide options connect to effect nodes")}
            template={template}
            onChange={(flow) =>
              updateTemplate({
                settings: {
                  ...(template.settings || {}),
                  flow,
                },
              })
            }
          />
        )}
      </div>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saveState.status === "saving"}
        isSuccess={saveState.status === "saved"}
        isError={saveState.status === "error"}
        errorMessage={saveState.error}
        onSave={save}
        saveLabel={t("Lagre guide")}
      />
    </div>
  );
}

function CaseGuideEffectsEditor({ effects, onChange, effectType, chainGuideCandidates = [] }) {
  const { t } = useTranslation();
  const patch = (nextPatch) => onChange({ ...(effects || {}), ...nextPatch });

  const tagsBlock = (
    <div>
      <p className="mb-1 text-xs font-medium text-gray-600">{t("Automatiske etiketter")}</p>
      <CaseTagSelector
        value={effects.tag_ids || []}
        variant="inline"
        onChange={(ids) => patch({ tag_ids: ids })}
        showBadges
      />
    </div>
  );

  if (!effectType) {
    return (
      <div className="mt-3 space-y-3">
        {tagsBlock}
        <GuideFlowField
          label={t("Priority")}
          value={effects.priority ? String(effects.priority) : ""}
          options={[{ value: "", label: "-" }, ...priorityOptions]}
          onChange={(value) => patch({ priority: value || undefined })}
        />
        <GuideFlowField
          label={`${t("User Assigned")} (ID)`}
          value={effects.user_assigned_id ? String(effects.user_assigned_id) : ""}
          onChange={(value) => patch({ user_assigned_id: value ? Number(value) : undefined })}
        />
        <GuideFlowField
          label={`${t("Ansvarlig person")} (ID)`}
          value={effects.user_responsible_id ? String(effects.user_responsible_id) : ""}
          onChange={(value) => patch({ user_responsible_id: value ? Number(value) : undefined })}
        />
        <GuideFlowField label={t("Forfallsdato")} type="date" value={effects.due_date || ""} onChange={(value) => patch({ due_date: value || undefined })} />
        <GuideFlowField label={t("Titteltillegg")} value={effects.title_suffix || ""} onChange={(value) => patch({ title_suffix: value })} />
        <GuideFlowField label={t("Erstatt tittel")} value={effects.title || ""} onChange={(value) => patch({ title: value })} />
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3">
      {effectType === "tags" ? tagsBlock : null}
      {effectType === "priority" ? (
        <GuideFlowField
          label={t("Priority")}
          value={effects.priority ? String(effects.priority) : ""}
          options={[{ value: "", label: "-" }, ...priorityOptions]}
          onChange={(value) => patch({ priority: value || undefined })}
        />
      ) : null}
      {effectType === "assignment" ? (
        <>
          <GuideFlowField
            label={`${t("User Assigned")} (ID)`}
            value={effects.user_assigned_id ? String(effects.user_assigned_id) : ""}
            onChange={(value) => patch({ user_assigned_id: value ? Number(value) : undefined })}
          />
          <GuideFlowField
            label={`${t("Ansvarlig person")} (ID)`}
            value={effects.user_responsible_id ? String(effects.user_responsible_id) : ""}
            onChange={(value) => patch({ user_responsible_id: value ? Number(value) : undefined })}
          />
        </>
      ) : null}
      {effectType === "due_date" ? (
        <GuideFlowField label={t("Forfallsdato")} type="date" value={effects.due_date || ""} onChange={(value) => patch({ due_date: value || undefined })} />
      ) : null}
      {effectType === "title_suffix" ? (
        <GuideFlowField label={t("Titteltillegg")} value={effects.title_suffix || ""} onChange={(value) => patch({ title_suffix: value })} />
      ) : null}
      {effectType === "chain_case_guide" ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-600">{t("guide.case_chain_effect_help")}</p>
          <GuideFlowField
            label={t("guide.case_chain_pick_guide")}
            value={effects.next_case_guide_template_id ? String(effects.next_case_guide_template_id) : ""}
            options={[
              { value: "", label: t("guide.case_chain_none") },
              ...chainGuideCandidates.map((g) => ({
                value: String(g.id),
                label: `${g.title || t("Uten navn")}${g.case_type_id ? "" : ` (${t("Generell caseguide")})`}`,
              })),
            ]}
            onChange={(value) => {
              const next = { ...(effects || {}) };
              if (value === "" || value == null) delete next.next_case_guide_template_id;
              else next.next_case_guide_template_id = Number(value);
              onChange(next);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function emptyTemplate(caseType, generalOnly = false) {
  const isGeneral = generalOnly || !caseType?.id;
  return {
    case_type_id: isGeneral ? null : caseType.id,
    purpose: "onboarding",
    title: isGeneral ? "" : caseType?.title ? `${caseType.title} guide` : "",
    description: "",
    active: true,
    version: 1,
    settings: {},
    questions: [],
  };
}
